// ────────────────────────────────────────────────────────────────────
// The inspection history table (design_handoff/README.md:189-196).
//
// ONE ROW PER INSPECTION DATE. Production stores one row per technique, so a
// tank inspected with UT and MT on the same day is two rows and one
// inspection; the server collapses them in `assemble.group_inspections` and
// this table renders that grain directly. The older
// `GET /api/assets/{ref}/inspection-history` (server/routers/corrosion.py:267)
// returns the technique grain and is deliberately NOT what this screen reads —
// it stays for its existing callers.
//
// Reading the record, not recomputing it: the RATE column's accent is driven
// by `rates.governed_by`, the server's answer under TOL = 0.0005, never by
// comparing two floats here.
//
// NOTHING IS DERIVED HERE, LOSS AND RATE INCLUDED. Both arrive per inspection
// as `loss_mm` / `rate_mm_yr`, computed by `rules.per_inspection_loss_and_rate`
// against the reading before them. LOSS was a subtraction in this file until
// the record carried it; RATE could never be one, because a per-row rate needs
// the decimal-year convention `rules.py` owns. Both are null on the first row
// — never 0, which would read as "no corrosion" where the truth is "no
// baseline" (design_handoff/README.md:319).
//
// ORDER — TWO SEPARATE DECISIONS, AND CONFLATING THEM IS WHAT BROKE THIS TABLE.
//   1. ARRAY order: the wire is NEWEST FIRST (assemble.group_inspections).
//   2. DISPLAY order: the handoff renders newest at the top — its prototype
//      does `d.rows.slice().reverse()` on an oldest-first array
//      (design_handoff/XZYNEXA.dc.html:2974), and README.md:189-196 gives the
//      current row the prominent `#fffaf3` + `3px #f0912e` treatment without
//      saying where it sits.
// They happen to agree, which is exactly why reading the current inspection off
// a POSITION was invisible: this file called `length - 1` current and so marked
// the OLDEST inspection as the current one on every real record. Both are now
// asked for by name (./order), and the current row is the one with the newest
// DATE — not a position, in either ordering.
// ────────────────────────────────────────────────────────────────────
import type { CSSProperties } from "react";
import { EMDASH, interval as fmtInterval, mm, rateMmYr } from "./format";
import { currentEvent, newestFirst } from "./order";
import type { AssetIntegrity } from "./types";

const HEAD: CSSProperties = {
  fontSize: 9.5, letterSpacing: ".09em", color: "var(--ink-4)",
  fontWeight: 400, textAlign: "left", padding: "7px 8px",
  borderBottom: "1px solid var(--hairline)", whiteSpace: "nowrap",
};
const CELL: CSSProperties = {
  fontSize: 11.5, color: "var(--ink-2)", padding: "9px 8px",
  borderBottom: "1px solid var(--line-3)", verticalAlign: "top",
};

export function InspectionHistoryTable({ record, openedDate = null, onOpen }: {
  record: AssetIntegrity;
  openedDate?: string | null;
  onOpen?: (date: string) => void;
}): JSX.Element {
  // Newest at the top, and the current inspection identified by its DATE.
  // Asking for the display order by name means this table renders the handoff's
  // order whatever order the wire arrives in, and `current` is right either way.
  const events = newestFirst(record.inspections);
  const currentDate = currentEvent(record.inspections)?.date ?? null;

  // Accent when the current row's rate is the governing rate. Read off the
  // server's verdict, so EQUAL counts (the two rates are the same rate) and
  // float noise never decides it. The most recent interval's rate IS the
  // short-term rate — that is what `rules.summarise_rates` measured it
  // between — so this asks about that row and no other.
  const shortTermGoverns =
    record.rates.governed_by === "SHORT-TERM" || record.rates.governed_by === "EQUAL";

  return (
    <section style={{ marginTop: 20 }}>
      <div className="font-mono"
           style={{ fontSize: 9.5, letterSpacing: ".1em", color: "var(--ink-4)", marginBottom: 6 }}>
        INSPECTION HISTORY
      </div>

      <div style={{ overflowX: "auto", border: "1px solid var(--hairline)", borderRadius: 6, background: "var(--paper-2)" }}>
        <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 900 }}>
          <colgroup>
            <col style={{ width: 82 }} />
            <col style={{ width: 132 }} />
            <col style={{ width: 86 }} />
            <col style={{ width: 74 }} />
            <col style={{ width: 92 }} />
            <col style={{ width: 104 }} />
            <col />
            <col style={{ width: 92 }} />
            <col style={{ width: 46 }} />
          </colgroup>
          <thead>
            <tr style={{ background: "var(--surface-2)" }}>
              <th className="font-mono" style={HEAD}>DATE</th>
              <th className="font-mono" style={HEAD}>SOURCE OF RECORD</th>
              <th className="font-mono" style={{ ...HEAD, textAlign: "right" }}>MIN READING</th>
              <th className="font-mono" style={{ ...HEAD, textAlign: "right" }}>LOSS</th>
              <th className="font-mono" style={HEAD}>RATE</th>
              <th className="font-mono" style={{ ...HEAD, textAlign: "right" }}>INTERVAL</th>
              <th className="font-mono" style={HEAD}>CONCLUSION</th>
              <th className="font-mono" style={HEAD}>SIGNED BY</th>
              <th className="font-mono" style={HEAD}>DETAIL</th>
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => {
              const current = ev.date === currentDate;
              const opened = openedDate != null && ev.date === openedDate;
              // Opened wins over current: it is the row the reader is looking at.
              const tint = opened ? "var(--surface-warm-2)"
                         : current ? "var(--surface-warm-3)" : "transparent";
              const edge = opened ? "inset 3px 0 0 var(--ink-1)"
                         : current ? "inset 3px 0 0 var(--accent-2)" : undefined;
              const rowRate = ev.rate_mm_yr;
              const accent = current && rowRate != null && shortTermGoverns;

              return (
                <tr key={ev.date}
                    data-testid="history-row"
                    data-current={current ? "true" : "false"}
                    data-opened={opened ? "true" : "false"}
                    onClick={() => onOpen?.(ev.date)}
                    style={{ background: tint, cursor: onOpen ? "pointer" : "default" }}>
                  <td className="font-mono" style={{ ...CELL, boxShadow: edge, color: "var(--ink-1)" }}>
                    {ev.date}
                  </td>
                  <td className="font-mono" style={CELL}>{ev.source_ref || EMDASH}</td>
                  <td className="font-mono" style={{ ...CELL, textAlign: "right", color: "var(--ink-1)" }}>
                    {mm(ev.min_reading_mm)}
                  </td>
                  <td className="font-mono" style={{ ...CELL, textAlign: "right" }}>
                    {mm(ev.loss_mm)}
                  </td>
                  <td data-testid="history-rate"
                      data-accent={accent ? "true" : "false"}
                      className="font-mono"
                      style={{ ...CELL, color: accent ? "var(--accent-deep)" : "var(--ink-3)" }}>
                    {rowRate == null ? EMDASH : rateMmYr(rowRate)}
                  </td>
                  <td className="font-mono" style={{ ...CELL, textAlign: "right" }}>
                    {current ? fmtInterval(record.interval.in_force?.months) : EMDASH}
                  </td>
                  <td style={CELL}>
                    {ev.conclusion || EMDASH}
                    {!ev.signer && (
                      // README.md:325 — the verdict is the engineer's written
                      // conclusion, and an unsigned draft must say so on its face.
                      <div className="font-mono"
                           style={{ fontSize: 9, letterSpacing: ".08em", color: "var(--warn)", marginTop: 3 }}>
                        CONDITIONS · UNSIGNED
                      </div>
                    )}
                  </td>
                  <td style={CELL}>{ev.signer || EMDASH}</td>
                  <td className="font-mono" style={{ ...CELL, color: "var(--accent-deep)", whiteSpace: "nowrap" }}>
                    {opened ? "OPEN ▸" : "▸"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="font-mono"
           style={{
             fontSize: 9.5, letterSpacing: ".09em", color: "var(--ink-4)",
             marginTop: 6, padding: "0 2px",
           }}>
        CLICK ANY ROW · THICKNESS IS ONE COLUMN OF AN INSPECTION, NOT THE WHOLE OF IT
      </div>
    </section>
  );
}
